import { Box, Grid, Stack, Typography } from '@mui/material';
import React, { memo, useEffect, useMemo, useState } from 'react';
import ComparisonCard from './BizviewComponents/ComparisonCard';

import PieChart from './BizviewComponents/PieChart';

import { useThemes } from '../Theme/theme';
import {
  useFetchBizviewDataQuery,
  useFetchListStockSummaryQuery,
  useFetchTotalClosingStockQuery,
} from '../../redux/apis/dashboardApi';
import AIBlinkButton from '../Buttons/AIBlinkButton';
import SimpleModal from '../modals/simpleModal';
import { axiosInstanceForLambda } from '../../library/Axios';
import { showWarningSnackbar } from '../snackBar/snakbar';
import API_ENDPOINTS from '../../apiEndpoints.js';
import BiZviewPeriodNew from './BizviewComponents/BiZviewPeriodNew.jsx';
import { useSelector } from 'react-redux';
import moment from 'moment';

const formatPeriodTitle = (period, listFYData = [], showFyString = true) => {
  const { startdate, enddate, financialyearid } = period;
  if (!startdate || !enddate) return '';

  const fyData = listFYData?.length > 0 ? listFYData?.find((fy) => fy.financialyearid === financialyearid) : null;

  if (!fyData) return '';

  const fyString = showFyString ? `FY ${fyData?.startyear.toString().slice(-2)} - ${fyData?.endyear.toString().slice(-2)}` : '';

  const start = moment(startdate * 1000);
  const end = moment(enddate * 1000);

  let dateString;
  if (start.isSame(end, 'day')) {
    dateString = ` (${start.format('MMM D')})`;
  } else {
    dateString = ` (${start.format('MMM D')} - ${end.format('MMM D')})`;
  }

  return fyString + dateString;
};

function BizView() {
  const { theme } = useThemes();
  const [aiModal, setAIModal] = useState(false);
  const [aiModalLoading, setAIModalLoading] = useState(false);
  const [aisummary, setAISummary] = useState('');

  const [titles, setTitles] = useState({
    current: null,
    previous: null,
    previewCurrent: null,
    previousCurrent: null,
  });

  const [bizviewVariables, setBizviewVariables] = useState({
    startdate: null,
    enddate: null,
    openingbalance: 1,
    companyid: null,
    financialyearid: null,
  });

  const [bizviewVariables2, setBizviewVariables2] = useState({
    startdate: null,
    enddate: null,
    openingbalance: 1,
    companyid: null,
    financialyearid: null,
  });

  const areQueryVariableOneValid = bizviewVariables ? Object.values(bizviewVariables).every((val) => val) : false;
  const areQueryVariableTwoValid = bizviewVariables2 ? Object.values(bizviewVariables2).every((val) => val) : false;

  const { data, isLoading: isLoadingData1 } = useFetchBizviewDataQuery(bizviewVariables, {
    skip: !areQueryVariableOneValid,
    refetchOnMountOrArgChange: true,
  });

  const { data: totalClosingStock1 = [] } = useFetchTotalClosingStockQuery(
    {
      endtimestamp: bizviewVariables?.enddate,
      financialyearid: bizviewVariables?.financialyearid,
    },
    { skip: !areQueryVariableOneValid }
  );

  const { data: data2, isLoading: isLoadingData2 } = useFetchBizviewDataQuery(bizviewVariables2, {
    skip: !areQueryVariableTwoValid,
    refetchOnMountOrArgChange: true,
  });

  const { data: totalClosingStock2 = [] } = useFetchTotalClosingStockQuery(
    {
      endtimestamp: bizviewVariables2?.enddate,
      financialyearid: bizviewVariables2?.financialyearid,
    },
    { skip: !areQueryVariableTwoValid }
  );

  // Removed topExpenses API calls as we're now using groupData directly

  const filteredData = (cb) => {
    setAIModal(true);
    setAIModalLoading(true);
    const bizData =
      data?.map((item) => ({
        group_name: item.group_name,
        balance: item.balance,
      })) || [];
    cb(bizData);
  };

  const filterApiData = async (data) => {
    const payload = {
      tab_name: 'Business Overview',
      data,
    };
    try {
      const res = await axiosInstanceForLambda.post(API_ENDPOINTS.REACT_APP_AIBLINK_URL, payload);
      setAISummary(res.data.markdown_summary);
      setAIModalLoading(false);
    } catch (err) {
      console.log(err);
      showWarningSnackbar('Something went wrong please try again');
      setAIModalLoading(false);
    }
  };
  const { listFYData } = useSelector((state) => state?.userPrefrenceandReducer);

  useEffect(() => {
    if (areQueryVariableOneValid) {
      setTitles((prev) => ({
        ...prev,
        current: formatPeriodTitle(bizviewVariables, listFYData),
        previewCurrent: formatPeriodTitle(bizviewVariables, listFYData, false),
      }));
    } else {
      setTitles((prev) => ({
        ...prev,
        current: '',
        previewCurrent: '',
      }));
    }

    if (areQueryVariableTwoValid) {
      setTitles((prev) => ({
        ...prev,
        previous: formatPeriodTitle(bizviewVariables2, listFYData),
        previewPrevious: formatPeriodTitle(bizviewVariables2, listFYData, false),
      }));
    } else {
      setTitles((prev) => ({
        ...prev,
        previous: '',
        previewPrevious: '',
      }));
    }
  }, [bizviewVariables, bizviewVariables2, listFYData, areQueryVariableOneValid, areQueryVariableTwoValid]);

  return (
    <>
      <Box sx={{ marginTop: '3px' }}>
        <Box sx={{ marginTop: '2px' }}>
          <Box
            sx={{
              width: '100%',
              borderRadius: 2,
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                alignItems: 'center',
                justifyContent: 'flex-end',
                ml: { xs: 0, md: 4 },
                px: 1,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <AIBlinkButton onClick={() => filteredData(filterApiData)} />
            </Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                paddingTop={1}
                spacing={{ xs: 1, md: '2px' }}
                alignItems="center"
                sx={{ width: { xs: '100%', sm: '100%', lg: '50%' } }}
              >
                <Box sx={{ width: '100%' }}>
                  <BiZviewPeriodNew onChange={setBizviewVariables2} defaultOffsetMonths={1} preview={titles.previewPrevious} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '10px' }}>VS</Typography>
                </Box>

                <Box sx={{ width: '100%' }}>
                  <BiZviewPeriodNew onChange={setBizviewVariables} preview={titles.previewCurrent} />
                </Box>
              </Stack>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: { xs: 0, md: 4 },
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                <AIBlinkButton
                  onClick={async () => {
                    filteredData(filterApiData);
                  }}
                />
              </Box>
            </Stack>
          </Box>

          <ComparisonCard
            loading={isLoadingData1 || isLoadingData2}
            currentData={areQueryVariableOneValid ? data || [] : []}
            previousData={areQueryVariableTwoValid ? data2 || [] : []}
            currentstockSummary={totalClosingStock1 || []}
            previousStockSummary={totalClosingStock2 || []}
          />
        </Box>
        <Box
          sx={{
            width: '100%',
            p: 0,
            mt: { sm: '8px', xs: 0 },
            overflow: 'hidden',
          }}
        >
          <Grid container alignItems="stretch" spacing={{ xs: 3, sm: 2, md: 0 }}>
            <Grid item xs={12} md={5.9}>
              <PieChart
                groupData={areQueryVariableTwoValid ? data2 || [] : []}
                title={titles.previous}
                id="chartdiv2"
                loading={isLoadingData2}
              />
            </Grid>

            <Grid item xs={12} md={0.2} sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              justifyContent: 'center', 
              alignItems: 'center' 
            }}>
              <Box
                sx={{
                  width: '2px',
                  height: '90%',
                  backgroundColor: theme.palette.divider,
                }}
              ></Box>
            </Grid>

            <Grid item xs={12} md={5.9}>
              <PieChart
                groupData={areQueryVariableOneValid ? data || [] : []}
                title={titles.current}
                type={'current'}
                id="chartdiv1"
                loading={isLoadingData1}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <SimpleModal open={aiModal} setOpen={setAIModal} title={'AI Insights'} body={aisummary} aiModalLoading={aiModalLoading} />
    </>
  );
}

export default memo(BizView);
